import React, { Component } from 'react';
import { useOnDemandVideo } from 'mobile-modules/offline/video';
import { logOfflineDiagnosticEvent } from 'mobile-modules/offline/assets';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import _ from 'lodash';

import { DraggableModal } from 'components/flink-components';
import { VideoPlayer } from 'components/common';
import { filesURL } from 'config';
import { isMobileAppRuntime } from 'utils';
import {
  showParentAdvice,
  setParentAdviceUrl,
  setParentAdviceCoords,
} from 'actions/flinkPlayActions';
import { showGameParentAdvice, showGameMultiplayerAdvice } from 'actions/gameActions';
import { stopAudio } from 'actions/audioActions';

import classes from './ParentAdvice.module.scss';
import { localizeMobileLoadingText, resolveMobileLoadingLocale } from 'components/common/mobileLoadingMessages';
// import { showError } from 'actions/statusActions';

// Only the native player is gated on its local cached URL. Web playback
// remains exactly as before, and the parent modal remains closable while
// a requested file downloads or the user is offline.
const OnDemandParentVideo = ({ source, className, onPlay, onError, diagnosticLocale }) => {
  const video = useOnDemandVideo(source, 'parent-video');
  const uiLocale = useSelector((state) => resolveMobileLoadingLocale(state).code);
  const displayedLocale = uiLocale || diagnosticLocale;
  const lastLocaleDiagnostic = React.useRef('');
  const renderedText = video.status === 'loading'
    ? 'Preparing video...'
    : video.status === 'offline-missing'
      ? 'This video has not been downloaded. Connect to the internet to watch it.'
      : video.status === 'ready' ? '' : 'Unable to load this video.';
  const localizedText = localizeMobileLoadingText(renderedText, displayedLocale);
  React.useEffect(() => {
    const signature = JSON.stringify([video.status, displayedLocale, localizedText]);
    if (lastLocaleDiagnostic.current === signature) return;
    lastLocaleDiagnostic.current = signature;
    const record = {
      screen: 'ParentAdvice',
      videoKind: 'parent-video',
      videoStatus: video.status,
      originalText: renderedText,
      renderedText: localizedText,
      solutionOrContentLocale: displayedLocale || '',
      localeSource: 'ParentAdvice.contentLocaleCode',
    };
    console.info('[FLC][loader-locale]', record);
    logOfflineDiagnosticEvent('mobile-loader-locale', record);
  }, [video.status, displayedLocale, renderedText, localizedText]);
  if (video.status !== 'ready') {
    return (
      <div
        className={`${className} ${classes.mobileVideoStatus}`}
        role={video.status === 'loading' ? 'status' : 'alert'}
      >
        {localizedText}
      </div>
    );
  }
  return (
    <video
      className={className}
      src={video.url}
      autoPlay
      controls
      playsInline
      onPlay={onPlay}
      onError={onError}
    />
  );
};

class ParentAdvice extends Component {
  state = {
    nextLocale: null,
    locale: null,
  };

  componentDidMount() {
    this.setUrl();
  }

  componentWillUnmount() {
    if (this.props.videos === undefined) return;

    const {
      parentAdviceUrl,
      isMenuAdviceOpened,
      setParentAdviceUrl,
      showParentAdvice,
    } = this.props;

    if (parentAdviceUrl) setParentAdviceUrl('');
    if (isMenuAdviceOpened) showParentAdvice(null);
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      // parentAdviceUrl,
      // isMenuAdviceOpened,
      isGameAdviceOpened,
      isGameMultiplayerAdviceOpened,
      currentActivity,
      currentMenu,
      contentLocaleCode,
      showSettings,
      familyReportsOpened,
      parentAdvicesEnabled,
      videos,
      forceEnabled,
    } = this.props;

    // if parent advice url is changed and locale is the same then
    // "screen" was changed and need to close dialog
    // if (
    //   prevProps.parentAdviceUrl &&
    //   parentAdviceUrl &&
    //   prevProps.parentAdviceUrl !== parentAdviceUrl &&
    //   prevProps.locale === locale &&
    //   prevState.locale === this.state.locale
    // ) {
    //   console.log(1)
    //   this.closeHandler();
    // }

    if (prevProps.contentLocaleCode !== contentLocaleCode && this.state.locale) {
      this.setState({ locale: null, nextLocale: null });
      return;
    }

    if (
      prevState.locale !== this.state.locale ||
      prevProps.contentLocaleCode !== contentLocaleCode ||
      prevProps.showSettings !== showSettings ||
      prevProps.familyReportsOpened !== familyReportsOpened ||
      // prevProps.isMenuAdviceOpened !== isMenuAdviceOpened ||
      prevProps.isGameAdviceOpened !== isGameAdviceOpened ||
      prevProps.isGameMultiplayerAdviceOpened !== isGameMultiplayerAdviceOpened ||
      prevProps.currentMenu !== currentMenu ||
      prevProps.currentActivity !== currentActivity ||
      prevProps.parentAdvicesEnabled !== parentAdvicesEnabled ||
      prevProps.videos !== videos ||
      prevProps.forceEnabled !== forceEnabled
    ) {
      this.setUrl();
    }
  }

  setUrl = () => {
    const {
      currentActivity,
      currentMenu,
      showSettings,
      configParentVideos,
      familyReportsOpened,
      parentAdvicesEnabled,
      isGameMultiplayerAdviceOpened,
      videos,
      forceEnabled,
    } = this.props;

    // Check if multiplayer advice is opened
    if (isGameMultiplayerAdviceOpened) {
      // Get settings multi-player video
      const videos = (configParentVideos && configParentVideos.multiplayer) || {};
      return this.setVideos(videos);
    }


    if (!parentAdvicesEnabled && !forceEnabled) {
      return this.resetHandler();
    }

    // Login can supply the Settings Parent Videos directly because no menu or
    // authenticated solution is selected yet on that screen.
    if (videos !== undefined) {
      return this.setVideos(videos || {});
    }

    // Check if current settings is opened
    if (showSettings) {
      // Get settings parent video
      const videos = (configParentVideos && configParentVideos.settings) || {};
      this.setVideos(videos);
    }

    // Check if family reports is opened
    else if (familyReportsOpened) {
      // Get settings parent video
      const videos =
        (configParentVideos && configParentVideos.familyReports) || {};
      this.setVideos(videos);
    }

    // Check if activity is opened
    else if (currentActivity) {
      const { parentVideos } = currentActivity.template;
      const { parentVideos: gameParentVideos } = currentActivity.data.options;

      this.setVideos(gameParentVideos || parentVideos || {});
    }

    // Check if menu is opened
    else if (currentMenu) {
      this.setVideos(currentMenu.parentVideos || {});
    }
  };

  setVideos = (videos) => {
    const {
      locales,
      parentAdviceUrl,
      setParentAdviceUrl,
      parentAdvicesLocales,
      contentLocaleForced,
    } = this.props;

    const baseLocaleCode = String(this.props.contentLocaleCode || '');
    const localeCode = this.state.locale?.code
      ? String(this.state.locale.code)
      : baseLocaleCode;
    const availableLocales = [];
    const sharedVideoKeys = new Set(['allLocales', '__all', '_all', 'all', 'shared']);
    const normalizeFilename = (value) => (
      typeof value === 'string' ? value.trim() : ''
    );

    const selectedRaw = Array.isArray(parentAdvicesLocales) ? parentAdvicesLocales : [];
    const selectedSet = new Set(
      selectedRaw
        .map((x) => (x && typeof x === 'object' ? x.code : x))
        .filter(Boolean)
        .map((x) => String(x).toLowerCase()),
    );

    const solutionHasSelectedLocalesForParentAdvice = selectedSet.size > 0 && !contentLocaleForced;

    const findLocaleObjByCode = (code) => {
      const c = String(code || '').toLowerCase();
      if (!c) return null;
      return (locales || []).find((l) => String(l?.code || '').toLowerCase() === c) || null;
    };

    const getSharedVideo = () => {
      if (typeof videos === 'string') return normalizeFilename(videos);
      if (!videos || typeof videos !== 'object') return '';

      for (const key of sharedVideoKeys) {
        const filename = normalizeFilename(videos[key]);
        if (filename) return filename;
      }

      const uniqueLegacyFilenames = Array.from(new Set(
        Object.keys(videos)
          .filter((key) => !sharedVideoKeys.has(key))
          .map((key) => normalizeFilename(videos[key]))
          .filter(Boolean),
      ));

      return uniqueLegacyFilenames.length === 1 ? uniqueLegacyFilenames[0] : '';
    };

    const sharedVideo = getSharedVideo();

    const getVideoByLocaleCode = (code) => {
      const c = String(code || '');
      if (!c) return sharedVideo || null;

      const direct = normalizeFilename(videos && videos[c]);
      if (direct) return direct;

      const key = Object.keys(videos || {}).find((x) => (
        !sharedVideoKeys.has(x)
        && String(x).toLowerCase() === c.toLowerCase()
      ));
      const explicit = key ? normalizeFilename(videos[key]) : '';

      return explicit || sharedVideo || null;
    };

    if (sharedVideo) {
      const localeCandidates = solutionHasSelectedLocalesForParentAdvice
        ? (locales || []).filter((loc) => selectedSet.has(String(loc?.code || '').toLowerCase()))
        : [findLocaleObjByCode(baseLocaleCode)].filter(Boolean);

      localeCandidates.forEach((loc) => {
        if (!availableLocales.some((item) => String(item?.code) === String(loc?.code))) {
          availableLocales.push(loc);
        }
      });
    } else {
      Object.keys(videos || {}).forEach((key) => {
        if (sharedVideoKeys.has(key)) return;

        const value = normalizeFilename(videos[key]);
        if (!value) return;

        const loc = findLocaleObjByCode(key);
        if (!loc) return;

        if (solutionHasSelectedLocalesForParentAdvice) {
          if (selectedSet.has(String(loc.code).toLowerCase())) {
            availableLocales.push(loc);
          }
        } else if (String(loc.code).toLowerCase() === baseLocaleCode.toLowerCase()) {
          availableLocales.push(loc);
        }
      });
    }

    let parentVideo = getVideoByLocaleCode(localeCode);
    let effectiveLocaleCode = localeCode;

    const localeIsAvailable = availableLocales.some((item) => (
      String(item?.code || '').toLowerCase() === String(localeCode || '').toLowerCase()
    ));

    // A shared filename is reused across language folders. Do not replace the
    // currently selected solution locale with the first configured advice locale.
    if ((!parentVideo || (!sharedVideo && !localeIsAvailable)) && availableLocales.length) {
      effectiveLocaleCode = String(availableLocales[0].code || '');
      parentVideo = getVideoByLocaleCode(effectiveLocaleCode);
    }

    if (!parentVideo) return this.resetHandler();

    let nextLocale;
    const idxOfLocale = _.findIndex(
      availableLocales,
      (l) => String(l?.code || '').toLowerCase() === String(effectiveLocaleCode || '').toLowerCase(),
    );

    if (availableLocales.length > 1) {
      nextLocale = idxOfLocale === availableLocales.length - 1
        ? availableLocales[0]
        : availableLocales[Math.max(0, idxOfLocale + 1)];
    }

    // The mobile modal can reopen while the same video URL is still stored in
    // Redux. Keep its language control in sync even when the URL does not
    // change. Preserve the existing desktop update path below.
    const mobileRuntime = isMobileAppRuntime();
    const currentNextLocaleCode = String(this.state.nextLocale?.code || '').toLowerCase();
    const nextLocaleCode = String(nextLocale?.code || '').toLowerCase();

    if (mobileRuntime && currentNextLocaleCode !== nextLocaleCode) {
      this.setState({ nextLocale: nextLocale || null });
    }

    const effectiveLocaleObj =
      (this.state.locale && String(this.state.locale.code || '').toLowerCase() === String(effectiveLocaleCode || '').toLowerCase())
        ? this.state.locale
        : findLocaleObjByCode(effectiveLocaleCode) || findLocaleObjByCode(baseLocaleCode);

    const localeFolder = effectiveLocaleObj?.name || effectiveLocaleObj?.displayName || effectiveLocaleObj?.code || 'en';
    const url = `${filesURL}/Files/animations/ParentVideos/${localeFolder}/${parentVideo}`;

    if (parentAdviceUrl !== url) {
      console.log('[ParentAdvice][url]', {
        contentLocaleCode: baseLocaleCode,
        effectiveLocaleCode,
        sharedFilename: sharedVideo || null,
        availableLocales: (availableLocales || []).map((l) => l.code),
        nextLocale: nextLocale ? nextLocale.code : null,
        url,
      });
      if (!mobileRuntime) this.setState({ nextLocale });
      setParentAdviceUrl(url);
    }
  };

  changeLocale = () => {
    this.setState({ locale: this.state.nextLocale });
  };

  errorHandler = () => {
    // this.closeHandler();
  };

  onStartPlay = () => {
    stopAudio();
  };

  resetHandler = () => this.closeHandler(true);

  closeMobileHandler = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    event?.nativeEvent?.stopImmediatePropagation?.();

    // Keep the modal mounted until the close input has fully finished so the
    // same tap cannot reach the page-flip surface underneath it.
    window.requestAnimationFrame(() => this.closeHandler());
  };

  closeHandler = (resetUrl = false) => {
    const {
      parentAdviceUrl,
      isGameAdviceOpened,
      isGameMultiplayerAdviceOpened,
      isMenuAdviceOpened,
      setParentAdviceUrl,
      showParentAdvice,
      showGameParentAdvice,
      showGameMultiplayerAdvice,
    } = this.props;

    parentAdviceUrl && resetUrl && setParentAdviceUrl('');
    isMenuAdviceOpened && showParentAdvice(null);
    isGameAdviceOpened && showGameParentAdvice(null);
    isGameMultiplayerAdviceOpened && showGameMultiplayerAdvice(null);

    // Every new opening must start from the currently selected solution locale.
    // The local state is used only while cycling languages inside the open modal.
    if (this.state.locale || this.state.nextLocale) {
      this.setState({ locale: null, nextLocale: null });
    }
  };

  debouncedSetParentAdvice = _.debounce(this.props.setParentAdviceCoords, 100);

  render() {
    const {
      parentAdviceUrl,
      isMenuAdviceOpened,
      isGameAdviceOpened,
      isGameMultiplayerAdviceOpened,
      parentAdvicesEnabled,
      parentAdviceCoords,
      forceEnabled,
    } = this.props;

    const { nextLocale } = this.state;
    const mobileRuntime = isMobileAppRuntime();

    if (!parentAdvicesEnabled && !forceEnabled && !isGameMultiplayerAdviceOpened) return null;
    if (!parentAdviceUrl) return null;

    if (!isMenuAdviceOpened && !isGameAdviceOpened && !isGameMultiplayerAdviceOpened) return null;

    return (
      <DraggableModal
        show
        noGutters
        resizable
        size={{ width: '50vmin' }}
        lockAspectRatio
        onClose={mobileRuntime ? this.closeMobileHandler : () => this.closeHandler()}
        {...(mobileRuntime ? {
          overlayClassName: classes.mobileOverlay,
          modalClassName: classes.mobileWrapper,
          contentClassName: classes.mobileVideoContent,
          preserveMediaDimensions: true,
        } : { className: classes.wrapper })}
        onDrag={(...args) => this.debouncedSetParentAdvice(...args)}
        position={parentAdviceCoords}
      >
        {mobileRuntime ? (
          <OnDemandParentVideo
            key={parentAdviceUrl}
            source={parentAdviceUrl}
            diagnosticLocale={this.props.contentLocaleCode}
            className={classes.mobileNativeVideo}
            onPlay={this.onStartPlay}
            onError={this.errorHandler}
          />
        ) : (
          <VideoPlayer
            // ref={this.videoRef}
            playing
            controls
            width="100%"
            height="100%"
            url={parentAdviceUrl}
            onPlay={this.onStartPlay}
            // onReady={() => setLoaded(true)}
            onError={this.errorHandler}
            // onEnded={callback}
          />
        )}

        {nextLocale && (
          <button
            className={classes.changeLangButton}
            onClick={this.changeLocale}
          >
            {nextLocale.displayName}
          </button>
        )}
      </DraggableModal>
    );
  }
}

ParentAdvice.propTypes = {
  locales: PropTypes.array.isRequired,
  isMenuAdviceOpened: PropTypes.bool,
  contentLocaleCode: PropTypes.string,
  contentLocaleForced: PropTypes.bool,
  forceContentLocale: PropTypes.bool,
  currentMenu: PropTypes.object,
  showParentAdvice: PropTypes.func.isRequired,
  showGameParentAdvice: PropTypes.func.isRequired,
  setParentAdviceUrl: PropTypes.func.isRequired,
  parentAdviceUrl: PropTypes.string,
  showSettings: PropTypes.bool,
  configParentVideos: PropTypes.object,
  familyReportsOpened: PropTypes.bool,
  parentAdvicesLocales: PropTypes.array,
  videos: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  forceEnabled: PropTypes.bool,
  // showError: PropTypes.func.isRequired,
};

const mapStateToProps = ({ flinkPlay, game, status, common }, ownProps) => {
  const explicitContentLocaleCode = String(ownProps.contentLocaleCode || '');
  const activityLocaleCode =
    game.currentActivity?.activity?.currentLanguageVersion?.locale || '';
  const solutionLocaleCode = flinkPlay.solutionLocale?.code || '';
  const statusLocaleCode = status.lang?.code || '';

  return {
    locales: common.locales,
    configParentVideos: common.config.parentVideos,
    showSettings: flinkPlay.showSettings,
    parentAdviceCoords: flinkPlay.parentAdviceCoords,
    familyReportsOpened: flinkPlay.familyReportsOpened,
    parentAdviceUrl: flinkPlay.parentAdviceUrl,
    currentActivity: game.currentActivity,
    currentMenu: flinkPlay.currentMenu,
    // In regular Play, Parent Advice follows the Solution interface/string
    // language. Flink Make preview may still pass an explicit content locale.
    contentLocaleCode:
      explicitContentLocaleCode
      || solutionLocaleCode
      || activityLocaleCode
      || statusLocaleCode,
    contentLocaleForced: ownProps.forceContentLocale === true,
    isMenuAdviceOpened: flinkPlay.parentAdvice,
    isGameAdviceOpened: game.gameParentAdvice,
    isGameMultiplayerAdviceOpened: game.gameMultiplayerAdvice,
    parentAdvicesEnabled:
      (flinkPlay.solution && flinkPlay.solution.parentAdvicesEnabled) ||
      game.isPreview,
    parentAdvicesLocales:
      (flinkPlay.solution && flinkPlay.solution.parentAdvicesLocales) || [],
  };
};

export default connect(mapStateToProps, {
  showParentAdvice,
  showGameParentAdvice,
  showGameMultiplayerAdvice,
  setParentAdviceUrl,
  setParentAdviceCoords,
  // showError
})(ParentAdvice);
